/**
 * Sample item for the Item Detail second-pass Lab page.
 *
 * Every value here is example content, not workspace data. It mirrors the
 * mockup published 2026-10-14 (claude.ai/artifact/RMCbiRhCqm1CVfeA7LaqHE) so the
 * Lab page and the mockup can be read side by side.
 */
import type { Tone } from '@/components/mono/primitives';

export type ItemState = 'triage' | 'active' | 'deferred' | 'archived';

export interface SampleCategory {
  id: string;
  category: string;
  altitude: 1 | 2 | 3 | 4 | 5;
  wording: string;
  note: string;
}

export interface GapRow {
  label: string;
  mark: string;
  tone: Tone;
  body: string;
  actions?: string[];
}

export interface PoolCheck {
  label: string;
  mark: string;
  tone: Tone;
  body: string;
}

export interface PoolBlock {
  cycle: string;
  pool: string;
  where: string;
  verdict: string;
  verdictTone: Tone;
  /** Index into READINESS_STEPS of the step this pool has reached, or null. */
  step: number | null;
  checks: PoolCheck[];
  sessions?: { date: string; name: string; verdict: string; tone: Tone }[];
}

export interface RelationshipRow {
  group: 'side' | 'updown';
  kind: string;
  text: string;
  strength?: string;
  verdict: string;
  tone: Tone;
}

/** The item exactly as one group saw it when they ranked it. */
export interface RankedAs {
  title: string;
  category: string;
  altitude: number;
  wording: string;
  values: string;
  /** What has changed on the item since; empty when nothing has. */
  changedSince: string[];
}

export interface SessionResult {
  name: string;
  where: string;
  verdict: string;
  tone: Tone;
  rankedAs: RankedAs;
  change?: { by: string; from: number; to: number; reason: string };
}

/** Canon 01 altitude names (1–5). */
export const ALTITUDE_NAMES: Record<number, string> = {
  1: 'Tactical',
  2: 'Operational',
  3: 'Program',
  4: 'Portfolio',
  5: 'Strategic',
};

export const ITEM_CATEGORIES = ['Problem', 'Opportunity', 'Risk', 'Solution', 'Commitment', 'Investment'];

/** The five readiness steps are still the 2026-09-22 proposal — not ruled on. */
export const READINESS_STEPS = [
  'Not ready',
  'Conditionally ready',
  'Ready to include',
  'Ready to compare',
  'Ready to rank',
];

export const SAMPLE = {
  title: 'Alternate Valve Supplier Qualification (dual-source)',
  description:
    'Qualify a second dispense-valve supplier with the corrected seat elastomer. Executes the July dual-source decision: first-article inspection, material certs, 500-hour syrup-exposure life test, and PPAP. Unblocks the CAPA-118 corrective action and de-risks the 77X-MF platform BOM.',
  owner: 'Ray Delgado',
  created: '25 Jul',
  updated: '13 Oct',
  tags: ['engineering-change', 'supply-chain', 'dual-source'],
  primary: {
    id: 'primary',
    category: 'Commitment',
    altitude: 4,
    wording: 'Qualify a second valve supplier through PPAP by 30 Nov.',
    note: 'Set by hand 21 Sep. Classifier proposes altitude 3 at 0.71, unreviewed.',
  } as SampleCategory,
  additional: [
    {
      id: 'risk',
      category: 'Risk',
      altitude: 4,
      wording: 'A single-source valve failure can stop the Dallas line again.',
      note: 'Fits the Operations risk track.',
    },
    {
      id: 'opportunity',
      category: 'Opportunity',
      altitude: 3,
      wording: 'Could a second supplier cut valve cost by 8% next year?',
      note: 'Fits the FY27 cost track.',
    },
  ] as SampleCategory[],
  criteriaWithValues: [
    { name: 'Reach', value: '8.4' },
    { name: 'Confidence', value: '6.0' },
  ],
  wellFormed: [
    {
      label: 'How it’s written',
      mark: '✓ Clear',
      tone: 'green',
      body: 'Written as an outcome with the problem named. Not a task, not a chosen solution.',
    },
    {
      label: 'Duplicate check',
      mark: '✕ Undecided',
      tone: 'coral',
      body: 'In the cluster “Valve second-source” with one other candidate. Nobody has decided: duplicate, variant, or the same item under another category.',
      actions: ['Decide', 'Open the cluster'],
    },
    {
      label: 'Attributes',
      mark: '! Partial',
      tone: 'mustard',
      body: 'A Commitment asks for work type, launch type and effort. Launch type and effort are not set.',
      actions: ['Set launch type', 'Set effort'],
    },
    {
      label: 'Values',
      mark: '! Guesses',
      tone: 'mustard',
      body: 'Reach 8.4 and Confidence 6.0 rest on nothing yet, and one re-score from Ana Ruiz is waiting on you.',
      actions: ['Attach evidence', 'Review the re-score', 'Mark a value unknown'],
    },
  ] as GapRow[],
  pools: [
    {
      cycle: 'Q3 FY26',
      pool: 'Q3 Engineering',
      where: 'Platform delivery track · takes Commitment × altitude 3–4 · not locked',
      verdict: 'Ready to include',
      verdictTone: 'mustard',
      step: 2,
      checks: [
        { label: 'Category × altitude', mark: '✓ Fits', tone: 'green', body: 'Its primary category, Commitment at altitude 4, is inside what this track takes.' },
        { label: 'Criteria here', mark: '! Missing', tone: 'mustard', body: 'This pool compares on Reach, Confidence and Effort. Effort has no value yet.' },
        { label: 'Competing', mark: '✕ Unsettled', tone: 'coral', body: '“Single-source price concession” is in this pool too, and only one of the two can be chosen. Settled in this track.' },
        { label: 'Package', mark: '! Flagged', tone: 'mustard', body: 'Its package partner “Life-test rig booking” is not in this pool. That is flagged, never refused; the package is settled where the two tracks meet.' },
      ],
      sessions: [
        { date: '18 Sep', name: 'Supply risk deep-dive · criteria: Reach, Risk reduction', verdict: 'Ready to rank here', tone: 'green' },
        { date: '25 Oct', name: 'Q3 commitment ranking · criteria include Effort', verdict: 'Needs effort', tone: 'mustard' },
      ],
    },
    {
      cycle: 'FY27 planning',
      pool: 'FY27 cost candidates',
      where: 'Cost track · takes Opportunity × altitude 3',
      verdict: 'Ready to compare',
      verdictTone: 'green',
      step: 3,
      checks: [
        { label: 'Through a category', mark: '✓ Fits', tone: 'green', body: 'Its primary category would not fit here. Its additional category, the Opportunity question at altitude 3, does, so it enters this pool in those words.' },
      ],
    },
    {
      cycle: 'Not in a cycle',
      pool: 'Supplier scenarios',
      where: 'No track, no cycle · used for scenario planning',
      verdict: 'Not compared yet',
      verdictTone: 'neutral',
      step: null,
      checks: [
        { label: 'Nothing to compare to', mark: 'Why', tone: 'neutral', body: 'This pool has no criteria, so readiness cannot be judged here. That is normal for a scenario pool.' },
      ],
    },
  ] as PoolBlock[],
  values: [
    { name: 'Reach', value: '8.4', note: 'No evidence', tone: 'mustard' as Tone },
    { name: 'Confidence', value: '6.0', note: '1 re-score waiting', tone: 'mustard' as Tone },
    { name: 'Effort', value: null, note: 'Not set', tone: 'neutral' as Tone },
    { name: 'Risk reduction', value: null, note: 'Not set', tone: 'neutral' as Tone },
  ],
  relationships: [
    { group: 'side', kind: 'Dependency', text: '77X-MF BOM revision, not started', strength: 'Hard', verdict: 'Blocks commitment', tone: 'coral' },
    { group: 'side', kind: 'Prerequisite', text: 'Seat elastomer spec sign-off', verdict: 'Met', tone: 'green' },
    { group: 'side', kind: 'Competing', text: 'Single-source price concession · settled in the track', verdict: 'Unsettled', tone: 'coral' },
    { group: 'side', kind: 'Package', text: 'With “Life-test rig booking” · all or nothing', verdict: 'Flagged', tone: 'mustard' },
    { group: 'side', kind: 'Cluster', text: '“Valve second-source” · 2 candidates', verdict: 'Undecided', tone: 'coral' },
    { group: 'side', kind: 'Related', text: 'Dispense head redesign', verdict: 'No weight', tone: 'neutral' },
    { group: 'updown', kind: 'Part of', text: 'Supply resilience programme', verdict: 'Altitude 5', tone: 'neutral' },
    { group: 'updown', kind: 'Breakdown', text: '3 parts: first article, life test, PPAP', verdict: '3', tone: 'neutral' },
    { group: 'updown', kind: 'Elaboration', text: 'Ways to meet it: tooling transfer approach · applies under the Commitment category', verdict: '1', tone: 'neutral' },
  ] as RelationshipRow[],
  sessionResults: [
    {
      name: 'Q3 commitment ranking',
      where: 'Q3 FY26 · Q3 Engineering pool · 25 Oct · Reach, Confidence, Effort',
      verdict: 'Result 4 → ranking 3',
      tone: 'mustard',
      change: { by: 'Maya Chen', from: 4, to: 3, reason: 'CAPA-118 closes only with a second source; this has to land in Q3.' },
      rankedAs: {
        title: 'Alternate Valve Supplier Qualification (dual-source)',
        category: 'Commitment',
        altitude: 4,
        wording: 'Qualify a second valve supplier through PPAP by 30 Nov.',
        values: 'Reach 8.4 · Confidence 7.1 · Effort 5.0',
        changedSince: [],
      },
    },
    {
      name: 'Supply risk deep-dive',
      where: 'Q3 FY26 · Q3 Engineering pool · 18 Sep · Reach, Risk reduction',
      verdict: 'Result 1 of 9 · not published',
      tone: 'slate',
      rankedAs: {
        title: 'Second valve supplier',
        category: 'Commitment',
        altitude: 4,
        wording: 'Line up a backup supplier for the dispense valve.',
        values: 'Reach 8.4 · Risk reduction 9.0',
        changedSince: ['Title renamed 21 Sep', 'Description rewritten 21 Sep'],
      },
    },
    {
      name: 'FY27 cost screen',
      where: 'FY27 planning · FY27 cost candidates · 2 Oct',
      verdict: 'Result 7 of 31 · below the cut',
      tone: 'mustard',
      rankedAs: {
        title: 'Alternate Valve Supplier Qualification (dual-source)',
        category: 'Opportunity',
        altitude: 3,
        wording: 'Could a second supplier cut valve cost by 8% next year?',
        values: 'Cost impact 6.5 · Confidence 5.0',
        changedSince: [],
      },
    },
  ] as SessionResult[],
};

/** Ruling → where it landed. Shown at the foot of the page. */
export const CHANGES_FROM_FIRST_PASS: { ruling: string; landed: string }[] = [
  { ruling: 'Buttons above the title', landed: 'Every action sits on one row above the title, all the same size. The title gets its own line and the description sits right under it.' },
  { ruling: 'No “comparison cell” in the header', landed: 'The header shows the category × altitude badge with no label. Inside a pool block the row is called “Category × altitude”.' },
  { ruling: 'Additional categories, not frames', landed: 'A “Categories” row in “Is it well formed?” shows the primary category and each additional one in its own words, as a statement or a question. It left Relationships, where it sat as “Reframe”.' },
  { ruling: 'No “shaping”', landed: '“Shape” became “How it’s written”. Nothing on the page says shaping or shaped.' },
  { ruling: 'Triage sets the state', landed: 'Each triage choice names the state it sets: active, deferred or archived. The header shows the state.' },
  { ruling: 'At least one criterion and a value', landed: '“Can it become a priority?” answers the rule in one line. Held for filing, not canon.' },
  { ruling: 'Altitude is 1 to 5', landed: 'Shown as a number with its canon name, for example 4 · Portfolio.' },
  { ruling: 'Item Manager and owner', landed: 'The rail says Owner for the person on this item and Item managers for the role.' },
  { ruling: 'Item pool stays separate', landed: 'Pool blocks say “item pool”. The no-cycle block is a scenario pool.' },
  { ruling: 'The list types', landed: '“Lists it’s in” in the rail counts collections, item pools, session results, priority lists and plans.' },
  { ruling: 'Result versus ranking', landed: 'After ranking, each session shows its result and the ranking that went forward, and any change with who and why.' },
  { ruling: 'Priority list is the only authority', landed: 'After ranking, “Is it a priority?” comes first and is the only place the page says so.' },
  { ruling: 'The plan holds a frozen copy', landed: '“In the plan” shows the plan of record and where its sequence differs from the rank.' },
  { ruling: 'Package is flagged, never refused', landed: 'The package row says “Flagged” and where it is settled.' },
  { ruling: 'What a group ranked stays as they saw it', landed: 'After ranking, each session shows “What this group ranked”: the title, category, wording and values as they were in that session, and what has changed on the item since. Two groups that ranked different wordings each see their own.' },
];
